<template>
  <action-dispatcher
    action="product/getProduct"
    :parameters="$route.params.id"
    class="view-email-logs"
  >
    <step-title
      title="Logs de Email"
      description="Acompanhe o histórico de envio de emails da área de membros."
    />

    <div class="py-6">
      <!-- Estatísticas -->
      <div class="row mb-5" v-if="stats">
        <div class="col-lg-3 col-md-6 mb-3">
          <div class="stat-card">
            <div class="stat-icon stat-icon-total">
              <i class="fa-solid fa-envelope"></i>
            </div>
            <div class="stat-content">
              <div class="stat-label">Total de Emails</div>
              <div class="stat-value">{{ stats.resumo.total }}</div>
            </div>
          </div>
        </div>
        <div class="col-lg-3 col-md-6 mb-3">
          <div class="stat-card">
            <div class="stat-icon stat-icon-sent">
              <i class="fa-solid fa-check-circle"></i>
            </div>
            <div class="stat-content">
              <div class="stat-label">Enviados</div>
              <div class="stat-value">{{ stats.resumo.enviados }}</div>
            </div>
          </div>
        </div>
        <div class="col-lg-3 col-md-6 mb-3">
          <div class="stat-card">
            <div class="stat-icon stat-icon-pending">
              <i class="fa-solid fa-clock"></i>
            </div>
            <div class="stat-content">
              <div class="stat-label">Pendentes</div>
              <div class="stat-value">{{ stats.resumo.pendentes }}</div>
            </div>
          </div>
        </div>
        <div class="col-lg-3 col-md-6 mb-3">
          <div class="stat-card">
            <div class="stat-icon stat-icon-failed">
              <i class="fa-solid fa-exclamation-circle"></i>
            </div>
            <div class="stat-content">
              <div class="stat-label">Falhas</div>
              <div class="stat-value">{{ stats.resumo.falhas }}</div>
              <!-- <div class="stat-subtitle">
                Taxa de sucesso: {{ stats.resumo.taxa_sucesso }}%
              </div> -->
            </div>
          </div>
        </div>
      </div>

      <!-- Filtros e Ações -->
      <div class="row justify-content-between mb-4">
        <div class="d-flex align-items-center col-lg-3 mb-3 mb-lg-0">
          <span class="table-quantity-counter mr-5">
            Total: {{ pagination.count || 0 }}
          </span>
          <button class="btn p-0 table-refresh" @click="refresh">
            <i class="fa-solid fa-refresh" />
          </button>
        </div>
        <div class="col-xl-7 col-lg-8">
          <div class="d-flex justify-content-between mb-3">
            <search-filter
              placeholder="Email, nome ou assunto"
              @search="searching"
              class="mb-3 mb-lg-0 flex-fill"
              filterButtonText=""
              :showResultsDropdown="false"
              :newDesign="true"
            />

            <filter-button
              button-class="btn-40 btn-square ml-3 btn-new-outline-primary btn-sm"
            >
              <template #default="{ close }">
                <email-logs-filter
                  v-model="filterValues"
                  @close="close"
                  @submit="applyFilters"
                />
              </template>
            </filter-button>

            <div class="d-none d-lg-block ml-3">
              <button
                class="btn btn-sm btn-outline-primary btn-40 btn-square btn-new-outline-primary"
                @click="exportToCSV"
                :disabled="exportLoading"
              >
                <i
                  class="fa-solid"
                  :class="exportLoading ? 'fa-spinner fa-spin' : 'fa-download'"
                />
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="text-center">
        <b-spinner variant="primary" />
      </div>

      <!-- Empty State -->
      <div
        v-else-if="!emailLogs.length"
        class="container centering text-center my-6"
      >
        <img
          class="d-block mb-9 mx-auto"
          src="@/assets/images/home/welcome.svg"
          style="max-width: 200px"
        />
        <h5>Nenhum log encontrado</h5>
        <div class="text-secondary mb-9">
          Os logs de envio de email aparecerão aqui quando os emails forem
          enviados.
        </div>
      </div>

      <!-- Table -->
      <div v-else>
        <!-- Desktop -->
        <data-table
          class="d-none d-lg-block"
          :columns="columns"
          :data="emailLogs"
          :columnsFr="'1.5fr 1.2fr 1.5fr 1fr 1fr 0.8fr 0.2fr'"
          :newDesign="true"
        >
          <template #default="{ item }">
            <div class="d-flex flex-column">
              <div class="email-cell">
                {{ formatLongText(item.recipient_name || "Sem nome", 30) }}
              </div>
              <small class="text-secondary">{{
                formatLongText(item.recipient_email, 30)
              }}</small>
            </div>
            <div class="d-flex align-items-center">
              {{ item.email_type_display }}
            </div>
            <div class="d-flex align-items-center">
              {{ formatLongText(item.subject, 40) }}
            </div>
            <div class="d-flex align-items-center">
              <email-log-status-badge
                :status="item.status"
                :status-display="item.status_display"
              />
            </div>
            <div class="d-flex align-items-center">
              {{ formatHumanDate(item.created_at) }}
            </div>
            <div class="d-flex align-items-center">
              {{ item.sent_at ? formatHumanDate(item.sent_at) : "-" }}
            </div>
            <div class="d-flex align-items-center justify-content-end">
              <button
                class="btn btn-sm btn-outline-secondary btn-icon"
                @click="openDetailsModal(item)"
              >
                <i class="fa-solid fa-eye"></i>
              </button>
            </div>
          </template>
        </data-table>

        <!-- Mobile -->
        <data-table
          class="d-lg-none"
          :columns="columns.slice(0, 2).map(() => '')"
          :data="emailLogs"
          :firstColumnFr="2"
          :lastColumnFr="0.1"
          :newDesign="true"
        >
          <template #default="{ item }">
            <div class="d-flex flex-column text-left pl-3 py-4">
              <div class="d-flex align-items-center pb-2">
                <strong class="mr-2">Destinatário:</strong>
                <span>{{ item.recipient_name || "Sem nome" }}</span>
              </div>
              <div class="d-flex align-items-center pb-2">
                <strong class="mr-2">Email:</strong>
                <span>{{ item.recipient_email }}</span>
              </div>
              <div class="d-flex align-items-center pb-2">
                <strong class="mr-2">Tipo:</strong>
                <span>{{ item.email_type_display }}</span>
              </div>
              <div class="d-flex align-items-center pb-2">
                <strong class="mr-2">Status:</strong>
                <email-log-status-badge
                  :status="item.status"
                  :status-display="item.status_display"
                />
              </div>
              <div class="d-flex align-items-center pb-2">
                <strong class="mr-2">Criado em:</strong>
                <span>{{ formatHumanDate(item.created_at) }}</span>
              </div>
            </div>
            <div class="d-flex justify-content-end">
              <button
                class="btn btn-sm btn-outline-secondary btn-icon"
                @click="openDetailsModal(item)"
              >
                <i class="fa-solid fa-eye"></i>
              </button>
            </div>
          </template>
        </data-table>
      </div>

      <!-- Pagination -->
      <div class="d-flex justify-content-center">
        <paginator
          :pagination="pagination"
          @on-update="onPaginationUpdate"
          v-show="emailLogs.length"
        />
      </div>
    </div>

    <!-- Modal de Detalhes -->
    <responsive-modal v-model="dialog.details" size="lg">
      <email-log-details-modal
        v-if="dialog.details && selectedLog"
        :log-id="selectedLog.id"
        @close="dialog.details = false"
      />
    </responsive-modal>
  </action-dispatcher>
</template>

<script>
import EmailLogsFilter from "@/components/product/emailLogs/EmailLogsFilter";
import EmailLogDetailsModal from "@/components/product/emailLogs/EmailLogDetailsModal";
import EmailLogStatusBadge from "@/components/product/emailLogs/EmailLogStatusBadge";
import { clearObject, toHumanDate } from "@/functions/helpers";
import moment from "moment";
import { mapState } from "vuex";

export default {
  components: {
    EmailLogsFilter,
    EmailLogDetailsModal,
    EmailLogStatusBadge
  },
  computed: {
    ...mapState("product", ["product"]),
    ...mapState("emailLogs", ["emailLogs", "pagination", "stats"]),
    breadcrumbs() {
      return [
        { link: "product", title: "Meus produtos" },
        {
          link: "product-details",
          title: this.product ? this.product["name"] : "...",
          params: { id: this.$route.params.id }
        },
        {
          title: "Logs de Email"
        }
      ];
    }
  },
  data() {
    return {
      params: {
        search: "",
        offset: null,
        email_type: "",
        status: "",
        created_at__gte: "",
        created_at__lte: "",
        ordering: "-created_at"
      },
      filterValues: {},
      exportLoading: false,
      loading: false,
      columns: [
        "Destinatário",
        "Tipo",
        "Assunto",
        "Status",
        "Criado em",
        "Enviado em",
        ""
      ],
      dialog: {
        details: false
      },
      selectedLog: null
    };
  },
  mounted() {
    this.loadStats();
    this.dispatch();
  },
  methods: {
    formatLongText(txt, maxLength = 26) {
      if (!txt) return "-";
      return txt.length > maxLength ? `${txt.slice(0, maxLength)}...` : txt;
    },
    formatHumanDate(date) {
      return toHumanDate(date);
    },
    searching(v) {
      this.params.search = v;
      this.dispatch();
    },
    applyFilters(filters) {
      this.filterValues = filters;
      this.params = {
        ...this.params,
        ...filters,
        offset: null
      };
      this.dispatch();
    },
    refresh() {
      this.loadStats();
      this.dispatch();
    },
    dispatch() {
      let payload = { ...this.params };

      this.loading = true;
      this.$store
        .dispatch("emailLogs/getAll", {
          product_id: this.$route.params.id,
          params: clearObject(payload)
        })
        .finally(() => {
          this.loading = false;
        });
    },
    loadStats() {
      let payload = { ...this.params };
      delete payload.offset;
      delete payload.search;

      this.$store.dispatch("emailLogs/getStats", {
        product_id: this.$route.params.id,
        params: clearObject(payload)
      });
    },
    onPaginationUpdate(offset) {
      this.params.offset = offset;
      this.dispatch();
    },
    openDetailsModal(log) {
      this.selectedLog = log;
      this.dialog.details = true;
    },
    async exportToCSV() {
      try {
        this.exportLoading = true;

        let payload = { ...this.params };
        delete payload.offset;

        const { data: csvContent } = await this.$store.dispatch(
          "emailLogs/exportCSV",
          {
            product_id: this.$route.params.id,
            params: clearObject(payload)
          }
        );

        const href = URL.createObjectURL(csvContent);
        const a = document.createElement("a");
        a.href = href;
        a.setAttribute(
          "download",
          `email_logs_${moment().format("YYYYMMDD")}_${moment().unix()}.csv`
        );
        a.click();

        this.$message.success("Exportação concluída com sucesso!");
      } catch (e) {
        this.$message.error("Erro ao exportar logs");
      } finally {
        this.exportLoading = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.view-email-logs {
  .btn {
    &:not(.btn-sm) {
      min-height: 28px;
    }
  }

  .stat-card {
    background: white;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.06);
    display: flex;
    align-items: center;
    gap: 16px;

    .stat-icon {
      width: 48px;
      height: 48px;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 20px;

      &.stat-icon-total {
        background: rgba(134, 54, 254, 0.1);
        color: #8636fe;
      }

      &.stat-icon-sent {
        background: rgba(72, 187, 120, 0.1);
        color: #2f855a;
      }

      &.stat-icon-pending {
        background: rgba(237, 137, 54, 0.1);
        color: #c05621;
      }

      &.stat-icon-failed {
        background: rgba(245, 101, 101, 0.1);
        color: #c53030;
      }
    }

    .stat-content {
      flex: 1;

      .stat-label {
        font-size: 13px;
        color: #77788e;
        margin-bottom: 4px;
      }

      .stat-value {
        font-size: 24px;
        font-weight: 700;
        color: #3b3b4a;
      }

      .stat-subtitle {
        font-size: 12px;
        color: #77788e;
        margin-top: 2px;
      }
    }
  }

  .email-cell {
    color: rgba(59, 59, 74, 0.9);
    line-height: 20px;
    font-weight: 500;
  }

  .btn-icon {
    width: 32px;
    height: 32px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
  }
}

.table-quantity-counter {
  font-weight: 500;
  color: #3b3b4a;
}

.table-refresh {
  border: none;
  background: none;
  color: #8636fe;
  cursor: pointer;
  font-size: 18px;

  &:hover {
    opacity: 0.8;
  }
}
</style>
